// Whether a change of pedigree selection would throw away the sidebar's
// unsaved section edits, and so has to ask first.
//
// THE DEFECT. The sidebar keeps a section's edits in that section's own state
// until the save footer's Save. Changing the selection re-seeds the sidebar:
// PersonMenu resets its form and marks itself clean on every new `data`, and a
// multi-selection or the family record swaps the person sections out for the
// family ones. So clicking another person on the canvas, finding one with
// Locate, lassoing a group or pressing Ctrl+A discarded the Person edits
// without a word. Diseases and Phenotypes were worse: their pending picks
// survived the switch and a later Save wrote them to the person selected NEXT.
//
// ChartBox owns the selection, so it is where the switch can be stopped before
// it happens. Every selection path there describes the selection it is about
// to make and goes through one gate, which asks the sidebar to confirm when
// this returns true (see `guardSelection` in ChartBox).
//
// Pure, so the rule is pinned by selectionGuard.test.js without mounting
// ChartBox, which no test in the repo can do.

/**
 * The record the sidebar shows for a selection: the pid it binds to, or null
 * for the family view. ChartBox hands the sidebar `data = null` whenever more
 * than one node is selected, so a multi-selection is the family view too.
 * Stringified because a saved member's pid is a number and a freshly added
 * one's is a UUID string.
 */
export const sidebarSubjectOf = ({ selectedCount = 0, dataPid = null } = {}) =>
  selectedCount > 1 || dataPid == null ? null : String(dataPid);

/**
 * True when the change from `current` to `next` would re-bind the sidebar to a
 * different record while it holds unsaved edits.
 *
 * Re-selecting the person already shown is NOT a switch, and must not ask:
 * clicking the node you are editing is the most natural thing to do mid-edit.
 * `keepSelectedData` below is what stops that click resetting the form anyway.
 * A caller with no `next` is not changing what the sidebar shows.
 */
export const selectionChangeNeedsConfirm = ({
  unsavedSections,
  current,
  next
} = {}) =>
  Array.isArray(unsavedSections) &&
  unsavedSections.length > 0 &&
  next != null &&
  sidebarSubjectOf(current) !== sidebarSubjectOf(next);

/**
 * Whether to keep the sidebar's existing `data` object instead of the new one
 * a selection path built. PersonMenu re-seeds its form from `data` on every new
 * object, so re-selecting the SAME person rebuilt `data` and discarded the
 * edits as surely as selecting someone else did. Only while something is
 * unsaved: a clean re-selection still refreshes `data` from memberRecords,
 * which is the only thing that picks up a non-structural change made elsewhere.
 */
export const keepSelectedData = (prev, next, unsavedSections) =>
  Array.isArray(unsavedSections) &&
  unsavedSections.length > 0 &&
  prev?.pid != null &&
  next?.pid != null &&
  String(prev.pid) === String(next.pid);

/**
 * The member a canvas node edits. A proxy is a render-only clone and the
 * sidebar binds to the record it stands for (see handleClick in ChartBox).
 */
export const ownerPidOf = (node) =>
  node == null
    ? null
    : node.isProxy && node.proxyOf != null
    ? node.proxyOf
    : node.pid ?? null;
